import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isNative, OAUTH_REDIRECT_NATIVE, OAUTH_REDIRECT_WEB } from '../../lib/platform';
import { Button, Spinner, Input, Logo } from '../../components/design-system';

/**
 * ForgotPassword — email-only request screen. Always lands on the "check
 * your email" state after submit, whether or not the address has an
 * account, so this can't be used to enumerate registered emails.
 *
 * The reset link points at the same auth/callback route (and, on native,
 * the same custom-scheme deep link) as OAuth and email confirmation — see
 * OAUTH_REDIRECT_NATIVE/WEB — where AuthCallback recognises `type=recovery`
 * and routes into ResetPassword instead of the app.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: isNative ? OAUTH_REDIRECT_NATIVE : OAUTH_REDIRECT_WEB,
      });
      // Supabase doesn't reveal whether the address has an account, so a
      // real error here is a genuine failure (rate limit, bad request) —
      // still worth surfacing rather than silently claiming success.
      if (resetError) throw resetError;
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh w-full" style={{ background: 'rgb(var(--violet))' }} data-kb-backdrop="violet">
      <div className="min-h-dvh flex flex-col items-center pt-safe-or-12 pb-safe-or-6 px-safe-or-6">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center text-center">
            <Logo variant="full" height={22} style={{ color: 'rgb(var(--cream-bright))' }} />
            <h1
              className="font-serif mt-6"
              style={{
                fontSize: 'var(--fs-page-title)',
                lineHeight: 'var(--lh-page-title)',
                letterSpacing: '-0.03em',
                color: 'rgb(var(--cream-bright))',
              }}
            >
              Reset your password
            </h1>
            <p className="text-sm mt-3" style={{ color: 'rgba(255, 252, 219, .7)' }}>
              {sent
                ? undefined
                : "Enter the email on your account and we'll send you a link to reset your password."}
            </p>
          </div>

          <div style={{ height: 32 }} />

          {sent ? (
            <div className="text-center">
              <h2 className="font-serif text-2xl mb-2" style={{ color: 'rgb(var(--cream-bright))' }}>Check your email</h2>
              <p className="text-sm" style={{ color: 'rgba(255, 252, 219, .7)' }}>
                If <strong style={{ color: 'rgb(var(--cream-bright))' }}>{email}</strong> has an account, we've sent a link to reset the password.
              </p>
              <div style={{ height: 24 }} />
              <Link
                to="/login"
                className="text-sm underline underline-offset-2 font-medium sk-hit"
                style={{ color: 'rgb(var(--cream-bright))' }}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                id="email"
                type="email"
                label="Email"
                tone="inverted"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
              {error && <p className="text-sm text-crimson">{error}</p>}
              <Button
                type="submit"
                size="xs"
                tone="inverted"
                fullWidth
                disabled={loading}
                className="mt-2"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    Sending…
                  </span>
                ) : (
                  'Send reset link'
                )}
              </Button>
              <Link
                to="/login"
                className="text-sm text-center underline underline-offset-2 font-medium mt-1 sk-hit"
                style={{ color: 'rgba(255, 252, 219, .7)' }}
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
